import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { getCRSTransactionHistoryData } from '../../../../redux/actions/crs/crsRiskDetail';
import { getAMLSTMCommonTransactionTypesFilter } from '../../../../redux/actions/tmCommon';
import { TransactionHistoryModule } from '../../../../services/crs/apiModules/RiskDetailModules/transactionHistoryModule';
import {
  getCodesFromLC,
  downloadReport
} from '../../../../services/util';
import { getDownloadTimezone } from '../../../../util/timezone';
import { CRS_CONSTANTS } from '../../../constants/crsContants';
import CRSDataTable from '../CRSProfileTables/CRSDataTable';
import { processDataFromProps } from '../riskDetailUtil';
import { transactionMappingBackup } from './constants';
import { formatTransactionDate } from './transactionDateFormat';
import TransactionSearchBar from '../../../common/TransactionDetalis/TransactionSearchBar';
import AdvancedFiltersPanel from '../../../common/TransactionDetalis/AdvancedFiltersPanel';
import DownloadDropdown from '../../../common/TransactionDetalis/DownloadDropdown';
import TransactionDetailsFilters from '../../../common/TransactionDetalis/TransactionDetailsFilters';
import {
  TransactionDetalisPanel,
  TransactionDetalisPanelTitle,
  SearchRow,
  MainFiltersRow,
  DownloadButtonWrapper,
  TableContainer
} from '../../../common/TransactionDetalis/styled';
import { notification, Select } from 'antd';
import { showExportReadyNotification } from '../../../common/components/ExportReadyNotification';

import { isEmpty } from '../../../../util/lodashlike';
const SortOrderToggle = ({ value, onChange }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      marginRight: 16,
      marginLeft: 8
    }}>
    <span style={{ fontSize: 11, color: '#748aa1', whiteSpace: 'nowrap' }}>
      Sort Transaction Date:
    </span>
    <Select
      size="small"
      value={value}
      onChange={onChange}
      style={{ width: 90, fontSize: 11 }}
      options={[
        { label: 'Newest', value: 'DESC' },
        { label: 'Oldest', value: 'ASC' }
      ]}
    />
  </div>
);

const { GET_DATA } = CRS_CONSTANTS;

const MIN_SEARCH_LENGTH = 3;

const buildSearchParams = (query) => {
  const params = {};
  if (query.searchValue && query.searchValue.length >= MIN_SEARCH_LENGTH) {
    params[query.searchField] = query.searchValue;
  }
  return params;
};

const buildAdvancedFilterParams = (query) => {
  const params = {};
  if (query.transactionType) params.transactionType = query.transactionType;
  if (query.accountId) params.accountId = query.accountId;
  if (query.sortOrder) params.sortOrder = query.sortOrder;
  return params;
};

/**
 * AssocTransaction Section component
 */
export const AssocTransaction = (props) => {
  const {
    alertId,
    customerKey,
    alertDate,
    GSConfig,
    crsRiskDetailData,
    dispatch,
    transactionTypes,
    isFromCM = false,
    moduleId,
    module: downloadModule,
    history,
    caseId
  } = props;

  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [query, setQuery] = useState({
    duration: 30,
    searchField: '',
    searchValue: '',
    transactionType: undefined,
    accountId: undefined,
    sortOrder: 'DESC'
  });
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const alertDateTO = useMemo(() => {
    return moment(alertDate ? new Date(alertDate) : new Date());
  }, [alertDate]);

  const alertDateFROM = useMemo(() => {
    return moment(alertDateTO)
      .subtract(query.duration ?? 30, 'days')
      .add(1, 'days');
  }, [alertDateTO]);

  useEffect(() => {
    setQuery((prev) => ({
      ...prev,
      from: alertDateFROM,
      to: alertDateTO
    }));
  }, [alertDateFROM, alertDateTO]);

  useEffect(() => {
    // Dispatch Redux action to fetch transaction types from tmCommon
    dispatch(
      getAMLSTMCommonTransactionTypesFilter({
        partyKey: customerKey
      })
    );
  }, [customerKey, dispatch]);

  useEffect(() => {
    const { from, to } = query;
    if (!from || !to || !customerKey) return;

    // Load transaction product mapping for potential future use
    const loadTransactionMapping = async () => {
      let mapping = await getCodesFromLC('transactionProductMapping');
      if (isEmpty(mapping)) {
        mapping = transactionMappingBackup;
      }
    };

    loadTransactionMapping();
  }, [dispatch, customerKey, query.from, query.to]);

  // Fetch transaction history when filters change
  useEffect(() => {
    const { from, to } = query;
    if (!from || !to) return;

    const searchParams = buildSearchParams(query);
    const advancedFilterParams = buildAdvancedFilterParams(query);

    dispatch(
      getCRSTransactionHistoryData(
        {
          customerKey,
          from: moment(from).startOf('day').valueOf(),
          to: moment(to).endOf('day').valueOf(),
          offset: 0,
          limit: currentPageSize,
          ...searchParams,
          ...advancedFilterParams
        },
        GET_DATA
      )
    );
  }, [
    customerKey,
    query.from,
    query.to,
    query.searchField,
    query.searchValue,
    query.transactionType,
    query.accountId,
    query.sortOrder,
    currentPageSize,
    dispatch
  ]);

  const onChangeWidgetAction = useCallback((values) => {
    setQuery((prev) => ({
      ...prev,
      ...values
    }));
    setCurrentPage(1);
  }, []);

  const onSearchChange = useCallback(({ searchField, searchValue }) => {
    setQuery((prev) => ({ ...prev, searchField, searchValue }));
    setCurrentPage(1);
  }, []);

  const onAdvancedFiltersApply = useCallback((advancedValues) => {
    setQuery((prev) => ({ ...prev, ...advancedValues }));
    setCurrentPage(1);
    setAdvancedFiltersOpen(false);
  }, []);

  const onPaginationChange = useCallback(
    async (page, pageSize) => {
      setCurrentPage(page);
      setCurrentPageSize(pageSize);
      const { from, to } = query;

      const searchParams = buildSearchParams(query);
      const advancedFilterParams = buildAdvancedFilterParams(query);

      dispatch(
        getCRSTransactionHistoryData(
          {
            customerKey,
            from: moment(from).startOf('day').valueOf(),
            to: moment(to).endOf('day').valueOf(),
            offset: (page - 1) * pageSize,
            limit: pageSize,
            isScrolled: true,
            ...searchParams,
            ...advancedFilterParams
          },
          GET_DATA
        )
      );
    },
    [customerKey, dispatch, query]
  );

  const handleDownload = useCallback(
    async ({ downloadAll = false } = {}) => {
      setIsDownloading(true);

      try {
        const { from, to } = query;
        const { CRS_BASE_URL, HTTP_HEADERS } = CRS_CONSTANTS;

        const searchParams = buildSearchParams(query);
        const advancedFilterParams = buildAdvancedFilterParams(query);

        const url = `${CRS_BASE_URL}/customers/${customerKey}/transactionHistory`;
        const fromEpoch = moment(from).startOf('day').valueOf();
        const toEpoch = moment(to).endOf('day').valueOf();
        const params = {
          alertId,
          from: fromEpoch,
          to: toEpoch,
          ...searchParams,
          ...advancedFilterParams
        };

        if (downloadAll) {
          // Async server-side export via TransactionHistoryModule
          const transactionHistoryModule = new TransactionHistoryModule(
            CRS_BASE_URL,
            HTTP_HEADERS
          );
          const tranData =
            await transactionHistoryModule.fetchTransactionHistory({
              alertId,
              customerKey,
              from: fromEpoch,
              to: toEpoch,
              isCM: isFromCM,
              moduleId,
              module: downloadModule,
              downloadAll: true,
              // FIN-12993: send the viewer timezone so the CRS export renders transaction dates
              // the same way this grid does ('utc' tenants keep the raw stored value)
              zoneId: getDownloadTimezone(),
              ...(caseId && { caseId }),
              ...searchParams,
              ...advancedFilterParams
            });

          if (tranData?.isExisting && tranData?.status === 'COMPLETED') {
            showExportReadyNotification(tranData, {
              onNavigate: () => {
                history.push({
                  pathname: `/app/crs/details/${customerKey}`,
                  search: '?tab=downloadHistory'
                });
              }
            });
          } else {
            notification.info({
              message: 'Export In Progress',
              description:
                tranData?.message ||
                "Your export is being prepared. You will be notified when it's ready."
            });
          }
        } else {
          // Sync CSV download from backend
          await downloadReport(url, HTTP_HEADERS, {
            ...params,
            download: true,
            offset: (currentPage - 1) * currentPageSize,
            limit: currentPageSize,
            moduleId,
            module: downloadModule,
            // FIN-12993: same viewer timezone as the async branch above
            zoneId: getDownloadTimezone(),
            ...(caseId && { caseId })
          });
        }
      } catch (error) {
        console.error('Download failed:', error);
      } finally {
        setIsDownloading(false);
      }
    },
    [
      customerKey,
      query,
      currentPage,
      currentPageSize,
      alertId,
      isFromCM,
      moduleId
    ]
  );

  const onDownloadClick = () => handleDownload();
  const onDownloadAllClick = () => handleDownload({ downloadAll: true });

  const getParamsFromResponse = () => {
    const { transactionHistoryResponse } =
      processDataFromProps(crsRiskDetailData);
    const { data, fetched, isFetching } = transactionHistoryResponse || {};
    const rows = data?.associated_transactions || [];
    const count = data?.count || 0;

    return {
      customerKey,
      rows,
      data,
      customerID: data?.customerId,
      isScrolled: data?.isScrolled,
      fetched,
      isFetching,
      count
    };
  };

  const { rows, isFetching, count } = getParamsFromResponse();

  // Hide if isVisible is set to false in global settings
  if (GSConfig?.isVisible === false) return null;

  return (
    <TransactionDetalisPanel>
      <TransactionDetalisPanelTitle>
        Transaction Details
      </TransactionDetalisPanelTitle>

      <SearchRow>
        <TransactionSearchBar
          onSearch={onSearchChange}
          advancedFiltersOpen={advancedFiltersOpen}
          onToggleAdvancedFilters={setAdvancedFiltersOpen}
          isTMTransaction={false}
          advancedFiltersOverlay={
            <AdvancedFiltersPanel
              filters={query}
              onApply={onAdvancedFiltersApply}
              onClose={() => setAdvancedFiltersOpen(false)}
              transactionTypes={transactionTypes}
              isTriggeredOnly={false}
              isTMTransaction={false}
            />
          }
        />
      </SearchRow>

      <MainFiltersRow>
        <TransactionDetailsFilters
          hasTriggeredOnlySwitch={false}
          showLabels={true}
          filters={query}
          onFilterChange={onChangeWidgetAction}
          alertDateTO={alertDateTO}
        />
        <SortOrderToggle
          value={query.sortOrder || 'DESC'}
          onChange={(order) =>
            setQuery((prev) => ({ ...prev, sortOrder: order }))
          }
        />
        <DownloadButtonWrapper>
          <DownloadDropdown
            onDownloadCurrentPage={onDownloadClick}
            onDownloadAll={onDownloadAllClick}
            isDownloading={isDownloading}
            isDisabled={!rows?.length || isFetching}
            currentPageCount={rows?.length || 0}
            totalCount={count || 0}
          />
        </DownloadButtonWrapper>
      </MainFiltersRow>

      <TableContainer>
        <CRSDataTable
          GSConfig={GSConfig}
          data={rows}
          loadMore={() => {}}
          loading={isFetching}
          hasMore={false}
          dataMapping={{
            isCredit: (row) =>
              row.amount == '' || row.amount === '-'
                ? ''
                : row.isCredit == null || row.isCredit == undefined
                ? '-'
                : row.isCredit
                ? 'CR'
                : 'DR',
            transactionDate: formatTransactionDate
          }}
          rowKey="id"
          pagination={{
            total: count,
            current: currentPage,
            pageSize: currentPageSize,
            onChange: onPaginationChange,
            showTotal: (total) => `${total} Records`,
            showSizeChanger: false
          }}
        />
      </TableContainer>
    </TransactionDetalisPanel>
  );
};

AssocTransaction.propTypes = {
  customerKey: PropTypes.string,
  GSConfig: PropTypes.object,
  crsRiskDetailData: PropTypes.object,
  dispatch: PropTypes.func,
  alertDate: PropTypes.string,
  transactionTypes: PropTypes.array,
  isFromCM: PropTypes.bool
};

/**
 * mapStateToProps
 * @param state
 * @param ownProps
 * @returns {{crsRiskDetailData: crsRiskDetailReducer, transactionTypes: array}}
 */
function mapStateToProps(state, ownProps = {}) {
  // Use the same transactionTypesFilter from amlsTMCommon as TransactionDetails
  const { transactionTypesFilter = {} } = state.amlsTMCommon || {};
  const { data: transactionTypesFilterData } = transactionTypesFilter;

  const { alertDetails = {} } = state.amlsCommon || {};
  const alertSourceId = alertDetails?.data?.alertSourceId;

  return {
    crsRiskDetailData: state.crsRiskDetailReducer,
    transactionTypes: transactionTypesFilterData || [],
    moduleId: alertSourceId
      ? Number(alertSourceId)
      : state.authentication?.currentProductLogin?.moduleId
  };
}

/**
 * mapDispatchToProps
 * @param dispatch
 * @param ownProps
 * @returns {{dispatch: *, navigateTo: navigateTo}}
 */
function mapDispatchToProps(dispatch, ownProps = {}) {
  return {
    dispatch
  };
}

/**
 * connect function for CRSRiskDetail page
 */
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(AssocTransaction));
